"use client";

import Image from "next/image";
import { Button } from "@heroui/button";
import { FaCloud } from "react-icons/fa";

import { logEvent } from "@/services/analytics";
import { getKeyString } from "@/utils/common";

export interface HeroCTAsProps {
  appleStoreUrl: string;
  googlePlayUrl: string;
  // Locale-prefixed app URL. Passed in from the server caller so the link
  // goes straight to the matching language (avoids the 307 redirect that
  // Ahrefs flags as a link-to-redirect SEO issue).
  browserAppUrl: string;
  labels: {
    browser: string;
    appStoreAria: string;
    googlePlayAria: string;
    secondaryStores: string;
  };
}

/**
 * Hero CTA — identical for EVERY visitor, so the homepage stays fully static
 * (no user-agent / platform detection). The primary button is the browser app
 * ("Start Faxing Free"); the App Store + Google Play badges below make it
 * clear Faxify is available on every platform. Because there's no client-side
 * platform detection, there's no hydration flicker and no layout shift — the
 * server HTML and the hydrated client render are identical.
 */
export function HeroCTAs({
  appleStoreUrl,
  googlePlayUrl,
  browserAppUrl,
  labels,
}: HeroCTAsProps) {
  return (
    // Outer wrapper centers the inner CTA column at every viewport size — the
    // hero's text column is centered too, so the CTAs share that visual axis.
    // Both the button and the badge row live in a fixed-width inner column so
    // their edges line up on every viewport size.
    <div className="mt-16 lg:mt-20 flex flex-col items-center">
      <div className="flex flex-col items-stretch gap-3 w-full max-w-[340px]">
        {/* Primary CTA — the browser app, shown to everyone. */}
        <Button
          as="a"
          className="w-full"
          color="primary"
          href={browserAppUrl}
          id="hero-primary-cta"
          radius="full"
          rel="noopener noreferrer"
          size="lg"
          startContent={<FaCloud />}
          target="_blank"
          onClick={() => logEvent(getKeyString("hero_browser_app_click"))}
        >
          {labels.browser}
        </Button>

        {/* Also available on the App Store + Google Play — shown to everyone. */}
        <div className="flex flex-col items-center gap-2 mt-2">
          <span className="text-sm text-default-600 whitespace-nowrap">
            {labels.secondaryStores}
          </span>
          <div className="flex items-center justify-center gap-4">
            <a
              aria-label={labels.appStoreAria}
              className="inline-block opacity-90 hover:opacity-100 transition-opacity"
              href={appleStoreUrl}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => logEvent(getKeyString("hero_app_store_click"))}
            >
              <Image
                alt={labels.appStoreAria}
                className="h-12 w-auto"
                height={48}
                src="/apple_app_store_banner.png"
                width={162}
              />
            </a>
            <a
              aria-label={labels.googlePlayAria}
              className="inline-block opacity-90 hover:opacity-100 transition-opacity"
              href={googlePlayUrl}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => logEvent(getKeyString("hero_play_store_click"))}
            >
              <Image
                alt={labels.googlePlayAria}
                className="h-12 w-auto"
                height={48}
                src="/google_play_store_banner.png"
                width={162}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
